interface Page {
  url: string;
  title: string;
  markdown: string;
}

interface PageListProps {
  pages: Page[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

export function PageList({ pages, selectedIndex, onSelect }: PageListProps) {
  if (pages.length <= 1) {
    return null;
  }

  return (
    <div className="w-72 flex-shrink-0 card overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-surface-700 bg-surface-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <span className="font-medium text-surface-200">Pages</span>
          </div>
          <span className="px-2 py-0.5 text-xs font-medium bg-primary-500/20 text-primary-400 rounded-md">
            {pages.length}
          </span>
        </div>
      </div>

      {/* Page List */}
      <div className="flex-1 overflow-auto p-2 space-y-1">
        {pages.map((page, index) => {
          const isSelected = selectedIndex === index;

          return (
            <button
              key={page.url}
              onClick={() => onSelect(index)}
              className={`w-full p-3 rounded-lg text-left transition-all duration-200 group relative ${
                isSelected
                  ? 'bg-primary-500/20 border border-primary-500/30'
                  : 'hover:bg-surface-700 border border-transparent hover:border-surface-700'
              }`}
            >
              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary-500 rounded-r-full" />
              )}

              {/* Page number */}
              <div className="flex items-start gap-3">
                <span className={`flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-xs font-medium ${
                  isSelected
                    ? 'bg-primary-500 text-white'
                    : 'bg-surface-700 text-surface-400 group-hover:bg-surface-600'
                }`}>
                  {index + 1}
                </span>

                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-sm truncate ${
                    isSelected ? 'text-primary-300' : 'text-surface-200 group-hover:text-surface-100'
                  }`}>
                    {page.title || 'Untitled'}
                  </div>
                  <div className={`text-xs truncate mt-0.5 ${
                    isSelected ? 'text-primary-400/70' : 'text-surface-500'
                  }`}>
                    {new URL(page.url).pathname}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer with scroll hint if many pages */}
      {pages.length > 5 && (
        <div className="px-4 py-2 border-t border-surface-700 bg-surface-800">
          <p className="text-xs text-surface-500 text-center">
            Scroll to see more pages
          </p>
        </div>
      )}
    </div>
  );
}
